import { useCallback, useRef, useState } from "react";
import { Entity } from "@common/types/entity";
import {
    ConsolidationGroupResponse,
    ConsolidationRelationshipResponse,
    CreateConsolidationRelationshipBody,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import {
    getConsolidationErrorMessage,
    replaceOwnershipRelationships,
} from "@api/consolidation.api";
import { classifyOwnershipProposal } from "./ownershipProposal.helpers";

interface PendingOwnershipUpdate {
    body: CreateConsolidationRelationshipBody;
    conflictingRelationshipIds: string[];
    confirmationBody: string;
}

interface UseOwnershipRelationshipCreateParams {
    workspaceId: string;
    relationships: ConsolidationRelationshipResponse[];
    groups: ConsolidationGroupResponse[];
    entitiesByUuid: Map<string, Entity>;
    createRelationship: (body: CreateConsolidationRelationshipBody) => void;
    onSaved: () => void;
    onError: (message: string) => void;
}

export function useOwnershipRelationshipCreate({
    workspaceId,
    relationships,
    groups,
    entitiesByUuid,
    createRelationship,
    onSaved,
    onError,
}: UseOwnershipRelationshipCreateParams) {
    const [pendingUpdate, setPendingUpdate] =
        useState<PendingOwnershipUpdate | null>(null);
    const [isReplacing, setIsReplacing] = useState(false);
    const isReplacingRef = useRef(false);

    const clearPendingUpdate = useCallback(() => {
        setPendingUpdate(null);
    }, []);

    const classifyBody = useCallback(
        (body: CreateConsolidationRelationshipBody) => {
            const sourceName =
                entitiesByUuid.get(body.sourceEntityUuid)?.name?.trim() ??
                "This entity";
            const targetName =
                entitiesByUuid.get(body.targetEntityUuid)?.name?.trim() ??
                "this entity";
            return classifyOwnershipProposal({
                sourceEntityUuid: body.sourceEntityUuid,
                targetEntityUuid: body.targetEntityUuid,
                relationships,
                groups,
                sourceName: sourceName === "" ? "This entity" : sourceName,
                targetName: targetName === "" ? "this entity" : targetName,
            });
        },
        [entitiesByUuid, groups, relationships],
    );

    const handleCreate = useCallback(
        (body: CreateConsolidationRelationshipBody) => {
            if (body.type !== ConsolidationEntityRelationshipType.ownership) {
                createRelationship(body);
                return;
            }

            const classification = classifyBody(body);
            switch (classification.kind) {
                case "duplicate":
                    onSaved();
                    return;
                case "blocked":
                    onError(classification.message);
                    return;
                case "create":
                    createRelationship(body);
                    return;
                case "confirm":
                    setPendingUpdate({
                        body,
                        conflictingRelationshipIds:
                            classification.conflictingRelationshipIds,
                        confirmationBody: classification.confirmationBody,
                    });
                    return;
                default: {
                    const exhaustiveCheck: never = classification;
                    return exhaustiveCheck;
                }
            }
        },
        [classifyBody, createRelationship, onError, onSaved],
    );

    const confirmPendingUpdate = useCallback(async () => {
        if (!pendingUpdate || isReplacingRef.current) {
            return;
        }

        const classification = classifyBody(pendingUpdate.body);
        switch (classification.kind) {
            case "duplicate":
                setPendingUpdate(null);
                onSaved();
                return;
            case "blocked":
                setPendingUpdate(null);
                onError(classification.message);
                return;
            case "create":
                setPendingUpdate(null);
                createRelationship(pendingUpdate.body);
                return;
            case "confirm":
                break;
            default: {
                const exhaustiveCheck: never = classification;
                return exhaustiveCheck;
            }
        }

        const conflictingRelationships = relationships.filter((relationship) =>
            classification.conflictingRelationshipIds.includes(relationship.id),
        );

        isReplacingRef.current = true;
        setIsReplacing(true);
        try {
            await replaceOwnershipRelationships({
                workspaceId,
                createBody: pendingUpdate.body,
                conflictingRelationships,
            });
            onSaved();
        } catch (error) {
            onError(
                getConsolidationErrorMessage(
                    error,
                    "Failed to update ownership relationships",
                ),
            );
        } finally {
            setPendingUpdate(null);
            setIsReplacing(false);
            isReplacingRef.current = false;
        }
    }, [
        classifyBody,
        createRelationship,
        onError,
        onSaved,
        pendingUpdate,
        relationships,
        workspaceId,
    ]);

    return {
        handleCreate,
        pendingUpdate,
        clearPendingUpdate,
        confirmPendingUpdate,
        isReplacing,
    };
}
